import React, { forwardRef, isValidElement, PureComponent, ReactElement, Ref } from 'react';

import { LinkAPI } from '@philips-emr/tws-components-api';
import { Size } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { DataContext, RouteContext } from '@philips-emr/tws-core/lib/component/context';
import { bool, element, eventListener, expression, func, number, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ClickEvent, HoverEvent } from '@philips-emr/tws-core/lib/component/types';
import HistoryService from '@philips-emr/tws-core/lib/services/history-service';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, isEnumValid, isFunction } from '@philips-emr/tws-utils';

import { withRouterContext } from '..';

import './link.scss';

export interface LinkProps {
  innerRef?: Ref<HTMLAnchorElement>;
  badge?: string;
  children?: string;
  className?: string;
  icon?: string | ReactElement;
  onClick?: (event: ClickEvent) => void;
  onContextMenuClick?: (event: ClickEvent) => void;
  onDoubleClick?: (event: ClickEvent) => void;
  onMouseEnter?: (event: HoverEvent) => void;
  onMouseLeave?: (event: HoverEvent) => void;
  onMouseMove?: (event: HoverEvent) => void;
  preventDefault?: boolean;
  route?: string;
  size?: Size;
  stopPropagation?: boolean;
  value: string;
}

@AwesomeComponent({ type: ComponentType.NAVIGATION, api: LinkAPI })
class Link extends PureComponent<LinkProps> {

  static contextType = DataContext.Context;

  static propTypes = {
    badge: string,
    children: oneOfType([string, number]).notEnumerable(),
    className: string,
    icon: oneOfType([string, element]),
    innerRef: func.notEnumerable(),
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onMouseEnter: eventListener,
    onMouseLeave: eventListener,
    onMouseMove: eventListener,
    preventDefault: bool.group('events'),
    route: string,
    size: oneOf(enumValues(Size)),
    stopPropagation: bool.group('events'),
    value: expression,
  };

  static defaultProps = {
    size: Size.SMALL,
  };

  getClassName(): string {
    const { className, size } = this.props;

    return classNames({
      'c-link': true,
      [`c-link--${size}`]: isEnumValid(Size, size),
      [`${className}`]: Boolean(className),
    });
  }

  onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, routeContext: RouteContext): void => {
    const { onClick, preventDefault, route, stopPropagation } = this.props;

    if (stopPropagation) {
      event.stopPropagation();
    }

    if (preventDefault) {
      event.preventDefault();
    }

    if (route) {
      const completeRoute = routeContext.nestedRoute(route);
      HistoryService.push(completeRoute, this.context);
    } else {
      isFunction(onClick) && onClick(new ClickEvent({
        preventDefault: () => event.preventDefault(),
        stopPropagation: () => event.stopPropagation(),
      }));
    }
  };

  onDoubleClick = (): void => {
    const { onDoubleClick } = this.props;

    isFunction(onDoubleClick) && onDoubleClick(new ClickEvent());

  };

  onContextMenuClick = (): void => {
    const { onContextMenuClick } = this.props;

    isFunction(onContextMenuClick) && onContextMenuClick(new ClickEvent());
  };

  renderIcon(): ReactElement | null {
    const { icon } = this.props;

    if (!icon) {
      return null;
    }

    if (isValidElement(icon)) {
      return icon;
    }

    // TODO: migrate withRouterContext to avoid using typings like this;
    const imageComponent = ({ href }: { href: string }): ReactElement => {
      return <img className="c-link__icon" src={href} />;
    };

    const ImageElement = withRouterContext(imageComponent, icon);

    return <ImageElement />;
  }

  renderText(): ReactElement | null {
    const { children, value } = this.props;

    const textValue = (React.Children.count(children) === 1) ? children : value;

    if (!textValue) {
      return null;
    }

    return <span className="c-link__text" x-contrast="cl">{msg(textValue)}</span>;
  }

  onMouseMove = (): void => {
    const { onMouseMove } = this.props;

    isFunction(onMouseMove) && onMouseMove(new HoverEvent());
  };

  onMouseLeave = (): void => {
    const { onMouseLeave } = this.props;

    isFunction(onMouseLeave) && onMouseLeave(new HoverEvent());
  };

  onMouseEnter = (): void => {
    const { onMouseEnter } = this.props;

    isFunction(onMouseEnter) && onMouseEnter(new HoverEvent());
  };

  getBadgeValue(): string | null {
    const { badge } = this.props;

    if (!badge) {
      return null;
    }

    const value = parseInt(badge);

    if (!isNaN(value) && value < 1) {
      return null;
    }

    return badge;
  }

  render(): ReactElement {
    const { innerRef } = this.props;

    return (
      <RouteContext.Context.Consumer>
        {routeContext => (
          <a
            className={this.getClassName()}
            data-badge={this.getBadgeValue()}
            onClick={(event) => this.onClick(event, routeContext)}
            onDoubleClick={() => this.onDoubleClick()}
            onContextMenu={() => this.onContextMenuClick()}
            onMouseOver={this.onMouseMove}
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            ref={innerRef}
          >
            {this.renderIcon()}
            {this.renderText()}
          </a>
        )}
      </RouteContext.Context.Consumer>
    );
  }
}

const linkWithRef = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => <Link {...props} innerRef={ref} />);

linkWithRef.displayName = Link.name;

export default linkWithRef;
